import React, { useState } from 'react';
import { Box, Typography, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import {
  LightMode,
  DarkMode,
  Search as SearchIcon,
  AttachFile as AttachFileIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ChatWithAnnotation, MediaCategory } from '@/types/api';
import { MEDIA_CATEGORY_FILTERS } from '@/utils/channelFilters';
import { useChatLabel } from '@/hooks/useChatLabel';
import { useChatTheme } from '../ChatThemeContext';
import DbStatusSelector from './DbStatusSelector';
import ChannelNoteEditor from './ChannelNoteEditor';

interface MessagePanelHeaderProps {
  chat: ChatWithAnnotation;
  /** null = no attachment constraint. */
  mediaType: MediaCategory | null;
  onMediaTypeChange: (mediaType: MediaCategory | null) => void;
  searchOpen: boolean;
  onToggleSearch: () => void;
}

const MessagePanelHeader: React.FC<MessagePanelHeaderProps> = ({
  chat,
  mediaType,
  onMediaTypeChange,
  searchOpen,
  onToggleSearch,
}) => {
  const { t } = useTranslation('pages');
  const { colors, mode, toggleMode } = useChatTheme();
  const chatLabel = useChatLabel();
  const name = chatLabel(chat);
  const [mediaAnchor, setMediaAnchor] = useState<null | HTMLElement>(null);

  const pickMedia = (next: MediaCategory | null) => {
    onMediaTypeChange(next);
    setMediaAnchor(null);
  };

  const renderMediaRow = (value: MediaCategory | null, label: string) => (
    <MenuItem
      key={value ?? 'all'}
      selected={mediaType === value}
      onClick={() => pickMedia(value)}
      sx={{ fontSize: '0.875rem', gap: 1.5 }}
    >
      <Box sx={{ flex: 1 }}>{label}</Box>
      {mediaType === value && <CheckIcon sx={{ fontSize: 16 }} />}
    </MenuItem>
  );

  return (
    <Box
      sx={{
        bgcolor: colors.headerBg,
        color: colors.headerText,
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 48,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {name}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          {t('chatPreview.sidebar.messageCount', { count: chat.message_count })}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        {/* Search is deliberately here and not in the sidebar: it covers this
            one open channel, which is what the header names. */}
        <Tooltip title={t('chatPreview.search.toggle')}>
          <IconButton
            onClick={onToggleSearch}
            size="small"
            aria-label={t('chatPreview.search.toggle')}
            sx={{
              color: colors.headerText,
              bgcolor: searchOpen ? 'rgba(255,255,255,0.2)' : 'transparent',
            }}
          >
            <SearchIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={t('chatPreview.mediaFilter.title')}>
          <IconButton
            onClick={(e) => setMediaAnchor(e.currentTarget)}
            size="small"
            aria-label={t('chatPreview.mediaFilter.title')}
            sx={{
              color: colors.headerText,
              bgcolor: mediaType ? 'rgba(255,255,255,0.2)' : 'transparent',
            }}
          >
            <AttachFileIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={mediaAnchor}
          open={Boolean(mediaAnchor)}
          onClose={() => setMediaAnchor(null)}
          slotProps={{ paper: { sx: { minWidth: 200 } } }}
        >
          <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}>
            {t('chatPreview.mediaFilter.title')}
          </Typography>
          {renderMediaRow(null, t('mediaCategories.all'))}
          {MEDIA_CATEGORY_FILTERS.map((category) =>
            renderMediaRow(category, t(`mediaCategories.${category}`))
          )}
          <Typography
            variant="caption"
            sx={{ px: 2, py: 0.75, display: 'block', maxWidth: 260, color: 'text.secondary', whiteSpace: 'normal' }}
          >
            {t('chatPreview.mediaFilter.note')}
          </Typography>
        </Menu>

        <DbStatusSelector
          chatId={chat.id}
          currentStatus={chat.annotation?.db_status || 'not_in_db'}
        />
        <ChannelNoteEditor
          chatId={chat.id}
          currentNote={chat.annotation?.notes || null}
        />
        <Tooltip title={mode === 'dark' ? t('chatPreview.theme.light') : t('chatPreview.theme.dark')}>
          <IconButton onClick={toggleMode} size="small" sx={{ color: colors.headerText }}>
            {mode === 'dark' ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default MessagePanelHeader;
